<template>
  <div class="settings">
    <div class="settings-header-top">
      <v-btn icon
             color="primary"
             class="margin-right-small"
             @click="onBackClick"
      >
        <v-icon>
          mdi-arrow-left
        </v-icon>
      </v-btn>
      <h1>Settings</h1>
    </div>

    <div class="settings-form">
      <v-select
        @change="changeAuthentication"
        :items="authenticationTypes"
        v-model="authentication"
        label="Authentication"
      ></v-select>

      <div class="users-settings"
           v-if="authentication == 'BuiltIn'"
      >
        <div class="settings-header">
          <h2>Users</h2>
          <AddUser
            :connection="connection"
            @users-updated="
              showSuccess('User added.');
              users = $event;"
            @failure="showFailure($event)"
          >
          </AddUser>
        </div>
        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">User name</th>
                <th class="text-left"
                    style="width: 104px;"
                ></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in users"
                  :key="user.password"
              >
                <td>{{ user.userName }}</td>
                <td>
                  <div>
                    <v-btn
                      v-if="user.id !== 'adf1d96d-5de3-4561-81d7-1fd73be5dc0e'"
                      icon
                      @click="removeUser(user)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                    <ChangeUserPassword
                      :user="user"
                      :connection="connection"
                      @users-updated="showSuccess('Password changed.')"
                    ></ChangeUserPassword>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>

      <div class="margin-top-five">
        <div class="settings-header">
          <h2>Location aliases</h2>
          <AddLocations
            :showSuccess="showSuccess"
            :showFailure="showFailure"
            :connection="connection"
            @failure="showFailure($event)"
            @new-locations="
              showSuccess('Location added.');
              locationAliases = $event;"
          >
          </AddLocations>
        </div>
        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left"
                    style="width: 25%"
                >Alias name</th>
                <th class="text-left"
                    style="width: 40%"
                >Path</th>
                <th class="text-left">Type</th>
                <th class="text-left"
                    style="width: 104px;"
                ></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="location in locationAliases"
                  :key="location.name"
                  @click="editLocation(location.path)"
              >
                <td>{{ location.name }}</td>
                <td>{{ location.path }}</td>
                <td>{{ AliasTypeNames[location.type] }}</td>
                <td>
                  <div>
                    <v-btn
                      icon
                      @click.stop="removeLocationAlias(location)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                    <EditLocations
                      :ref="location.path"
                      :location="location"
                      :showSuccess="showSuccess"
                      :showFailure="showFailure"
                      :connection="connection"
                      @locations-updated="
                        showSuccess('Location updated.');
                        locationAliases = $event;"
                      @failure="showFailure($event);"
                    >
                    </EditLocations>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>

        </v-simple-table>

      </div>

      <div class="margin-top-five"
           v-if="authentication != 'None'"
      >
        <div class="settings-header">
          <h2>User groups</h2>
          <AddUserGroup
            :connection="connection"
            @groups-updated="
              showSuccess('User group added.');
              userGroups = $event;"
            @failure="showFailure($event);"
          ></AddUserGroup>
        </div>

        <div v-for="group in userGroups"
             :key="group.groupName"
             class="margin-top-two"
        >
          <v-divider></v-divider>
          <div class="row row--space-between"
               style="margin-left:1px; margin-top: 10px"
          >
            <h3>{{ group.groupName }}</h3>
            <v-btn icon
                   class="margin-right-small"
                   v-if="group.id !== 'ad59f39f-728a-41e0-8bd3-60439ca0e0a4'"
            >
              <v-icon @click="removeUserGroup(group)">mdi-delete</v-icon>
            </v-btn>
          </div>

          <v-select v-if="authentication == 'BuiltIn'"
                    :items="users"
                    item-value="id"
                    item-text="userName"
                    multiple
                    v-model="group.builtInUsers"
                    @change="setBuiltInUsersInUserGroup(group, group.builtInUsers)"
                    label="Users"
          ></v-select>

          <div class="row row--space-between row--center"
               v-if="authentication == 'Windows'"
          >
            <div style="width: 80%; margin-left: 12px">
              <v-select
                :items="allWindowsUsers"
                multiple
                v-model="group.windowsUsers"
                @change="setWindowsUsersInUserGroup(group, group.windowsUsers)"
                label="Users"
              ></v-select>
            </div>

            <AddUserToGroup
              :connection="connection"
              :group="group"
              @groups-updated="
                showSuccess('User added.');
                userGroups = $event;"
              @failure="showFailure($event);"
            >
            </AddUserToGroup>
          </div>

          <v-select
            class="margin-bottom-two"
            :items="locationAliases"
            item-value="id"
            item-text="name"
            multiple
            v-model="group.allowedLocations"
            @change="setAllowedLocationsInUserGroup(group)"
            label="Allowed locations"
          ></v-select>

          <v-checkbox
            label="Allow all local drives"
            v-model="group.allDrivesAllowed"
            @change="updateAllowDrives(group.id, group.allDrivesAllowed)"
          ></v-checkbox>

          <v-checkbox
            label="Allow UNC Locations"
            v-model="group.uncLocationsAllowed"
            @change="updateAllowUncLocations(group.id, group.uncLocationsAllowed)"
          ></v-checkbox>
        </div>
      </div>


      <div class="margin-top-five">
        <div class="settings-header">
          <h2>Quality levels</h2>
          <AddQualityLevel
            :connection="connection"
            @encode-updated="
              showSuccess('Quality level added.');
              encode = $event;"
            @failure="showFailure($event);"
          >
          </AddQualityLevel>
        </div>

        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">Width</th>
                <th class="text-left">Bitrate</th>
                <th class="text-left"
                    style="width: 68px;"
                ></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="level in encode.levels"
                  :key="level.width - level.bitrate"
              >
                <td>{{ level.width }} pixels</td>
                <td>{{ formatBitrate(level.bitrate) }}</td>
                <td>
                  <v-btn
                    icon
                    @click="removeQualityLevel(level)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>

      <div class="margin-top-five">
        <div class="settings-header">
          <h2>Displayed qualities</h2>
        </div>

        <v-checkbox
          label="Allow advanced interlace modes"
          v-model="encode.showDeinterlace"
          @change="updateShowDeinterlace(encode.showDeinterlace)"
        ></v-checkbox>

        <v-checkbox
          label="Allow best quality modes"
          v-model="encode.showBestQuality"
          @change="updateShowBestQuality(encode.showBestQuality)"
        ></v-checkbox>
      </div>

      <div class="margin-top-five">
        <div class="settings-header">
          <h2>File browser</h2>
        </div>

        <v-combobox
          :items="[...defaultExclusions, ...fileBrowser.excludedFiles]"
          v-model="fileBrowser.excludedFiles"
          label="Excluded files and directories"
          multiple
          small-chips
          hide-selected
          @change="updateExcludedFiles(fileBrowser.excludedFiles)"
        >
          <template v-slot:selection="{ attrs, item, parent, selected }">
            <v-chip
              v-bind="attrs"
              :input-value="selected"
              label
              small
              class="pr-1"
            >
              <span class="pr-2">{{ item }}</span>
              <v-icon small
                      @click="parent.selectItem(item)"
              >mdi-close</v-icon>
            </v-chip>
          </template>
        </v-combobox>

        <v-checkbox v-if="authentication == 'None'"
                    label="Allow all local drives"
                    v-model="fileBrowser.alwaysAllowDrives"
                    @change="updateAllowDrivesGlobal(fileBrowser.alwaysAllowDrives)"
        ></v-checkbox>

        <v-checkbox v-if="authentication == 'None'"
                    label="Allow UNC Locations"
                    v-model="fileBrowser.alwaysAllowUncLocations"
                    @change="updateAllowUncLocationsGlobal(fileBrowser.alwaysAllowUncLocations)"
        ></v-checkbox>

        <v-checkbox label="Limit object storage results"
                    v-model="fileBrowser.limitObjectStorageResults"
                    @change="updateLimitObjectStorageResults(fileBrowser.limitObjectStorageResults)"
        ></v-checkbox>

      </div>

      <div class="margin-top-five">
        <div class="settings-header">
          <h2>File closing</h2>
        </div>

        <div class="row row--space-between row--center">
          <v-text-field style="width: 80%; margin-left: 12px; margin-right: 12px;"
                        label="Seconds of inactivity"
                        v-model.number="containerIdleTimeout"
          ></v-text-field>
          <v-btn text
                 @click="updateContainerIdleTimeout(containerIdleTimeout)"
          >Change</v-btn>
        </div>
      </div>

      <div class="margin-top-five">
        <div class="settings-header">
          <h2>Image sequence</h2>
        </div>

        <v-checkbox
          label="Enable image sequences"
          v-model="imageSequence"
          @change="updateImageSequence(imageSequence)"
        ></v-checkbox>
      </div>

      <div class="margin-top-five">
        <div class="settings-header">
          <h2>Resizing</h2>
        </div>

        <v-select
          @change="updateInterpolation(interpolation)"
          :items="interpolations"
          item-text="1"
          item-value="0"
          v-model="interpolation"
          label="Interpolation method"
        ></v-select>
      </div>

      <div class="margin-top-five">
        <div class="settings-header">
          <h2>Surround Channel Mappings</h2>
          <AddSurroundChannelMapping
            :connection="connection"
            :surroundChannelMappings="surroundChannelMappings"
            @surround-mappings-updated="
              showSuccess('Surround Channel Mapping added.');
              surroundChannelMappings = $event"
            @failure="showFailure($event)"
          >
          </AddSurroundChannelMapping>
        </div>

        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">Channel count</th>
                <th class="text-left">Mapping</th>
                <th class="text-left"
                    style="width: 68px;"
                ></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="[channelCount, mapping] in Object.entries(surroundChannelMappings)"
                  :key="channelCount"
              >
                <td>{{ channelCount }}</td>
                <td>{{ mappingToString(mapping) }}</td>
                <td>
                  <div class="channel-mappings-table-edit">
                    <v-btn
                      icon
                      @click="removeSurroundChannelMapping(channelCount)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                    <EditSurroundChannelMapping
                      :connection="connection"
                      :channelCount="Number(channelCount)"
                      :mapping="mapping"
                      :surroundChannelMappings="surroundChannelMappings"
                      @surround-mappings-updated="
                        showSuccess('Surround Channel Mapping updated.');
                        surroundChannelMappings = $event"
                      @failure="showFailure($event)"
                    >
                      >
                    </EditSurroundChannelMapping>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>

      <div class="margin-top-five">
        <div class="settings-header">
          <h2>Overlay</h2>
          <UpdateOverlayForm
            :connection="connection"
            :overlay="overlay"
            @failure="showFailure($event)"
            @overlay-updated="updateOverlay($event)"
          >
          </UpdateOverlayForm>
        </div>
        <OverlayFileForm
          :connection="connection"
          :overlay="overlay"
          @failure="showFailure($event)"
          @overlay-updated="updateOverlay($event)"
          @remove-overlay-file="removeOverlayFile"
        >
        </OverlayFileForm>
        <div v-if="overlay && overlay.fileContent"
             class="overlay-container"
        >
          <img :src="overlayImgSrcType + overlay.fileContent"
               class="overlay"
          >
        </div>
      </div>

      <div class="margin-top-five">
        <div class="settings-header">
          <h2>Cache</h2>
        </div>
        <UpdateCacheSettingsForm
          :connection="connection"
          :cacheSettings="cacheSettings"
          @failure="showFailure($event)"
          @cache-settings-updated="
            showSuccess('Cache Settings updated.');
            cacheSettings = $event;
          "
        >
        </UpdateCacheSettingsForm>
      </div>

      <GpuSettings v-if        = "gpuDecode !== null"
                   :gpu-decode = "gpuDecode"
                   :gpu-preset = "gpuPreset"
                   :ui-access  = "uiAccess"
                   :connection = "connection"
                   @failure    = "showFailure($event)"
                   @success    = "showSuccess('GPU decode ' + $event);"
                   @encode     =  "encode=$event"
      ></GpuSettings>

      <div class="margin-top-five">
        <div class="settings-header">
          <h2>Plugins</h2>

          <v-btn
            @click="uploadPlugin"
            color="primary"
          >
            <v-icon class="margin-right-small">mdi-plus-circle</v-icon>
            Upload plugin
          </v-btn>
        </div>

        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">Plugin name</th>
                <th class="text-left"
                    style="width: 80px;"
                ></th>
                <th class="text-left"
                    style="width: 50px;"
                ></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="plugin in plugins"
                  :key="plugin.name"
              >
                <td>{{ plugin.name }}</td>
                <td>
                  <v-chip
                    class="ma-2"
                    label
                    small
                    color="primary"
                  >
                    {{ pluginTypeName(plugin.pluginType) }}
                  </v-chip>
                </td>
                <td>
                  <div>
                    <v-btn
                      icon
                      v-if="plugin.removable"
                      @click="removePlugin(plugin.name)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>

      <div class="margin-top-five">
        <div class="settings-header">
          <h2>Experimental features</h2>
        </div>

        <v-checkbox
          label   = "Scopes"
          v-model = "experimentalScopes"
          @change = "updateExperimentalScopes(experimentalScopes);"
        >
        </v-checkbox>

        <v-checkbox
          label   = "Annotations"
          v-model = "experimentalAnnotations"
          @change = "updateExperimentalAnnotations(experimentalAnnotations);"
        >
        </v-checkbox>

      </div>

      <div class="margin-top-five mb-5">
        <div class="settings-header">
          <h2>Export / Import Settings</h2>
        </div>

        <v-row class="mt-5 ml-1">
          <v-btn
            color="primary"
            @click = "exportSettings"
          >
            <v-icon class="mr-3">mdi-export</v-icon>
            Export Settings
          </v-btn>
        </v-row>
        <v-row class="mt-8 ml-1">
          <v-btn
            color="primary"
            @click = "$refs.importSelection.value = null; $refs.importSelection.click();"
          >
            <v-icon class="mr-3">mdi-import</v-icon>
            Import Settings
          </v-btn>
        </v-row>
      </div>

    </div>

    <v-snackbar
      bottom
      v-model="adminPasswordChangeNeeded"
      timeout="-1"
      color="orange"
    >
      User 'admin' has default password. For security purposes, it is recommended to change it.
    </v-snackbar>

    <v-snackbar
      v-model="redSnackbar.isVisible"
      :timeout="redSnackbar.timeout"
      color="red"
    >
      <div v-if="typeof redSnackbar.text === 'string'">
        {{ redSnackbar.text }}
      </div>
      <div v-if="Array.isArray(redSnackbar.text)">
        <div v-for="line in redSnackbar.text"
             :key = "line"
        >
          {{ line }}
        </div>
      </div>

      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind = "attrs"
          @click = "redSnackbar.isVisible = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <v-snackbar
      v-model="greenSnackbar.isVisible"
      :timeout="greenSnackbar.timeout"
      color="green"
    >
      <div v-if="typeof greenSnackbar.text === 'string'">
        {{ greenSnackbar.text }}
      </div>
      <div v-if="Array.isArray(greenSnackbar.text)">
        <div v-for="line in greenSnackbar.text"
             :key = "line"
        >
          {{ line }}
        </div>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind = "attrs"
          @click = "greenSnackbar.isVisible = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <v-snackbar
      top
      v-model="isRestartNeeded"
      timeout="-1"
      color="orange"
    >
      Restart the Telestream GLIM service to apply changes.
    </v-snackbar>

    <!-- For file imports. -->
    <input
      ref       = "importSelection"
      type      = "file"
      accept    = ".json"
      style     = "display: none"
      multiple  = "multiple"
      @change   = "onImportSettings"
    >
  </div>
</template>

<script>
import {ConnectionService} from "../services/connection.service";
import {AllInterpolations, AllGpuPresets} from "../services/encoding-service";
import AddUser from "../components/settings/AddUser";
import ChangeUserPassword from "../components/settings/ChangeUserPassword";
import AddLocations from "../components/settings/AddLocations";
import AddUserGroup from "../components/settings/AddUserGroup";
import EditLocations from "../components/settings/EditLocations";
import AddUserToGroup from "../components/settings/AddUserToGroup";
import {AliasTypeNames} from "../services/locations.services";
import AddQualityLevel from "../components/settings/AddQualityLevel";
import AddSurroundChannelMapping from "../components/settings/AddSurroundChannelMapping";
import EditSurroundChannelMapping from "../components/settings/EditSurroundChannelMapping";
import {SET_EXPERIMENTAL_FEATURES} from "../utils/mutation-types"
import UpdateOverlayForm from "../components/settings/UpdateOverlayForm";
import OverlayFileForm from "../components/settings/OverlayFileForm";
import UpdateCacheSettingsForm from "../components/settings/UpdateCacheSettingsForm";
import axios from "axios";
import {AllowedViews} from "../services/allowed-views-service";
import {mapState} from 'vuex';
import GpuSettings from "@/components/settings/GpuSettings";

export default {
  name:       "Settings",
  components: {
    GpuSettings,
    AddUser,
    ChangeUserPassword,
    AddLocations,
    EditLocations,
    AddUserGroup,
    AddUserToGroup,
    AddQualityLevel,
    AddSurroundChannelMapping,
    EditSurroundChannelMapping,
    UpdateOverlayForm,
    UpdateCacheSettingsForm,
    OverlayFileForm
  },
  data() {
    return {
      connection:      null,
      authentication:  "None",
      users:           [],
      locationAliases: [],
      userGroups:      [],

      authenticationTypes: ["None", "BuiltIn", "Windows", "OpenIdConnect"],

      greenSnackbar: {isVisible: false, timeout: 0, text: ""},
      redSnackbar:   {isVisible: false, timeout: 0, text: ""},

      AliasTypeNames,

      interpolations: AllInterpolations,
      interpolation:  AllInterpolations[0],
      gpuPresets:     AllGpuPresets,
      gpuPreset:      AllGpuPresets[0],
      encode:         {levels: [], interpolation: AllInterpolations[0], gpuPreset: AllGpuPresets[0]},

      containerIdleTimeout: 0,
      imageSequence:        false,
      container:            {IdleTimeout: 0, imageSequence: false, temporaryStorage: { path: null, maxSizeGB: 1 }},

      experimentalScopes:      false,
      experimentalAnnotations: false,

      surroundChannelMappings: {},

      overlay:           this.getDefaultOverlay(),
      overlayImgSrcType: 'data:image/svg+xml;base64,',
      fileBrowser:       {excludedFiles: []},
      defaultExclusions: [
        ".DS_Store",
        "._.DS_Store",
        ".apdisk",
        ".Snapshot",
        ".TemporaryItems",
        ".Trash-1000",
        "Thumbs.db",
        "$RECYCLE.BIN",
        ".StorNext",
        ".Trashes"
      ],

      cacheSettings: {memory: {}, disk: { enabled: false }},

      isRestartNeeded:           false,
      AllowedViews,
      adminPasswordChangeNeeded: false,
      gpuDecode:                 null,

      plugins: []
    };
  },
  computed: {
    allWindowsUsers() {
      return [...new Set(this.userGroups.flatMap(userGroup => userGroup.windowsUsers))];
    },
    ...mapState(['uiAccess']),
  },
  async mounted() {
    this.connection = ConnectionService.getAutoReconnectConnection("/settings");
    this.handleRestartRequired();
    this.handleIsAdminPasswordChangeNeeded();
    await this.connection.start();
    await this.getSettings();
  },
  async beforeDestroy() {
    await this.connection?.stop();
  },
  methods: {
    onBackClick() {
      this.$router.push('/')
    },
    async getSettings() {
      const settingsResult = await this.connection.invoke("GetSettings");

      if (settingsResult.resultKind !== "Success") {
        this.showFailure(settingsResult.reason);
        return;
      }

      const settings = settingsResult.entity;

      console.log(settings)

      this.authentication = settings.authentication;
      this.users = settings.users;
      this.locationAliases = settings.locationAliases;
      this.userGroups = settings.userGroups;
      this.encode = settings.encode;
      this.interpolation = settings.encode.interpolation;
      this.gpuPreset = settings.encode.gpuPreset;
      this.container = settings.container;
      this.containerIdleTimeout = settings.container.idleTimeout;
      this.imageSequence = settings.container.imageSequence;
      this.experimentalScopes = settings.experimentalFeatures.scopes;
      this.experimentalAnnotations = settings.experimentalFeatures.annotations;
      this.surroundChannelMappings = settings.surroundChannelMappings;
      this.overlay = settings.overlay;
      this.overlayImgSrcType = this.overlay.type == 'Svg' ? 'data:image/svg+xml;base64,' : 'data:image/png;base64,'
      this.fileBrowser = settings.fileBrowser;
      this.isRestartNeeded = settings.restartNeeded;
      this.adminPasswordChangeNeeded = settings.adminPasswordChangeNeeded;
      this.cacheSettings = settings.cacheSettings;
      this.gpuDecode = settings.encode.gpuDecode;

      await this.getPlugins();
    },
    setBuiltInUsersInUserGroup(userGroup, newUsers) {
      this.connection
        .invoke("SetBuiltInUsersInUserGroup", userGroup.id, newUsers)
        .then((response) => {
          if (response.resultKind === "Success") {
            this.userGroups = response.entity;
            this.showSuccess('Users updated.');
          } else {
            this.showFailure(response.reason);
          }
        })
        .catch((reason) => {
          this.showFailure(reason);
        })
    },
    setWindowsUsersInUserGroup(userGroup, newCustomUsers) {
      this.connection
        .invoke("SetWindowsUsersInUserGroup", userGroup.id, newCustomUsers)
        .then((response) => {
          if (response.resultKind === "Success") {
            this.userGroups = response.entity;
            this.showSuccess('Users updated.');
          } else {
            this.showFailure(response.reason);
          }
        })
        .catch((reason) => {
          this.showFailure(reason);
        })
    },
    setAllowedLocationsInUserGroup(userGroup) {
      return this.connection
        .invoke(
          "SetAllowedLocationsToUserGroup",
          userGroup.id,
          userGroup.allowedLocations
        )
        .then(() => this.showSuccess('Allowed users updated.'))
        .catch((reason) => {
          this.showFailure(reason);
        });
    },
    changeAuthentication(newAuthentication) {
      this.connection
        .invoke("SetAuthentication", newAuthentication)
        .then((response) => {
          if (response.resultKind === "Success") {
            this.showSuccess("Authentication will change after GLIM restart.");
          } else {
            this.showFailure(response.reason);
          }
        })
        .catch((reason) => {
          this.showFailure(reason);
        })
    },
    removeUser(user) {
      if (confirm('Are you sure you want to remove the user: ' + user.userName) === false) {
        return;
      }
      this.connection
        .invoke("RemoveUser", user.id)
        .then((response) => {
          if (response.resultKind === "Success") {
            const result = response.entity;
            this.users = result.users;
            this.userGroups = result.userGroups;
            this.showSuccess(`User: '${user.userName}' removed.`);
          } else {
            this.showFailure(response.reason);
          }
        })
        .catch((reason => {
          this.showFailure(reason);
        }));
    },
    removeLocationAlias(alias) {
      if (confirm('Are you sure you want to remove the location: ' + alias.name) === false) {
        return;
      }
      this.connection
        .invoke("RemoveLocationAlias", alias.id)
        .then((response) => {
          if (response.resultKind === "Success") {
            const result = response.entity;
            this.locationAliases = result.aliases;
            this.userGroups = result.userGroups;
            this.showSuccess(`Location: '${alias.name}' removed.`);
          } else {
            this.showFailure(response.reason);
          }
        })
        .catch(reason => {
          this.showFailure(reason);
        });
    },
    removeUserGroup(group) {
      if (confirm('Are you sure you want to remove the group: ' + group.groupName) === false) {
        return;
      }
      this.connection
        .invoke("RemoveUserGroup", group.id)
        .then((response) => {
          if (response.resultKind === "Success") {
            this.userGroups = response.entity;
            this.showSuccess(`Group: '${group.groupName}' removed.`);
          } else {
            this.showFailure(response.reason);
          }
        })
        .catch(reason => {
          this.showFailure(reason);
        });
    },
    showFailure(reason, timeout = 60000) {
      this.redSnackbar.timeout = timeout;
      this.redSnackbar.text = reason;
      this.redSnackbar.isVisible = true;
    },
    showSuccess(reason, timeout = 5000) {
      this.greenSnackbar.timeout = timeout;
      this.greenSnackbar.text = reason;
      this.greenSnackbar.isVisible = true;
    },
    formatBitrate(bitrate) {
      if (bitrate >= 1000 * 1000) {
        return (bitrate / (1000 * 1000)) + " Mbit/s"
      } else if (bitrate > 1000) {
        return (bitrate / 1000) + " kbit/s"
      } else {
        return bitrate + " bit/s"
      }
    },
    removeQualityLevel(level) {
      return this.connection
        .invoke("RemoveQualityLevel", level)
        .then((response) => {
          if (response.resultKind === "Success") {
            this.encode = response.entity;
            this.showSuccess('Quality levels updated.');
          } else {
            this.showFailure(response.reason);
          }
        })
        .catch(reason => {
          this.showFailure(reason);
        });
    },
    updateInterpolation(newInterpolation) {
      return this.connection
        .invoke("UpdateInterpolation", newInterpolation)
        .then((response) => {
          if (response.resultKind === "Success") {
            const updated = response.entity;
            this.interpolation = updated.interpolation;
            this.encode = updated;
            this.showSuccess('Interpolation updated.');
          } else {
            this.showFailure(response.reason);
          }
        })
        .catch(reason => {
          this.showFailure(reason);
        });
    },
    updateContainerIdleTimeout(newContainerIdleTimeout) {
      return this.connection
        .invoke("UpdateContainerIdleTimeout", newContainerIdleTimeout)
        .then((response) => {
          if (response.resultKind === "Success") {
            const updated = response.entity;
            this.containerIdleTimeout = updated.idleTimeout;
            this.container = updated;
            this.showSuccess('Timeout updated.');
          } else {
            this.showFailure(response.reason);
          }
        })
        .catch(reason => {
          this.showFailure(reason);
        });
    },
    updateExperimentalScopes(enable) {
      return this.connection
        .invoke("UpdateExperimentalScopes", enable)
        .then((response) => {
          if (response.resultKind === "Success") {
            const updated = response.entity;
            this.experimentalScopes = updated.scopes;
            this.$store.commit(SET_EXPERIMENTAL_FEATURES, updated);
            this.showSuccess('Experimental features updated.');
          } else {
            this.showFailure(response.reason);
          }
        })
        .catch(reason => {
          this.showFailure(reason);
        });
    },
    updateExperimentalAnnotations(enable) {
      return this.connection
        .invoke("UpdateExperimentalAnnotations", enable)
        .then((response) => {
          console.log(response);
          if (response.resultKind === "Success") {
            const updated = response.entity;
            this.experimentalAnnotations = updated.annotations;
            this.$store.commit(SET_EXPERIMENTAL_FEATURES, updated);
            this.showSuccess('Experimental features updated.');
          } else {
            this.showFailure(response.reason);
          }
        })
        .catch(reason => {
          this.showFailure(reason);
        });
    },
    removeSurroundChannelMapping(channel) {
      return this.connection
        .invoke("RemoveSurroundChannelMapping", channel)
        .then((response) => {
          if (response.resultKind === "Success") {
            this.surroundChannelMappings = response.entity;
            this.showSuccess(`Surround Channel Mapping: '${channel}' removed.`);
          } else {
            this.showFailure(response.reason);
          }
        })
        .catch(reason => {
          this.showFailure(reason);
        });
    },
    updateExcludedFiles(excludedFiles) {
      return this.connection
        .invoke("UpdateExcludedFiles", excludedFiles)
        .then((response) => {
          if (response.resultKind === "Success") {
            const updated = response.entity;
            this.fileBrowser = updated;
            this.showSuccess('Excluded files updated.');
          } else {
            this.showFailure(response.reason);
          }
        })
        .catch(reason => {
          this.showFailure(reason);
        });
    },
    updateAllowDrives(groupId, newAllow) {
      return this.connection
        .invoke("UpdateAllowDrives", groupId, newAllow)
        .then((response) => {
          if (response.resultKind === "Success") {
            this.showSuccess('Always allow drives updated.');
          } else {
            this.showFailure(response.reason);
          }
        })
        .catch(reason => {
          this.showFailure(reason);
        });
    },
    updateAllowUncLocations(groupId, newAllow) {
      return this.connection
        .invoke("UpdateAllowUncLocations", groupId, newAllow)
        .then((response) => {
          if (response.resultKind === "Success") {
            this.showSuccess('Always allow UNC Locations updated.');
          } else {
            this.showFailure(response.reason);
          }
        })
        .catch(reason => {
          this.showFailure(reason);
        });
    },
    updateAllowDrivesGlobal(newAllow) {
      return this.connection
        .invoke("UpdateAllowDrivesGlobal", newAllow)
        .then((response) => {
          if (response.resultKind === "Success") {
            const updated = response.entity;
            this.fileBrowser = updated;
            this.showSuccess('Always allow drives updated.');
          } else {
            this.showFailure(response.reason);
          }
        })
        .catch(reason => {
          this.showFailure(reason);
        });
    },
    updateAllowUncLocationsGlobal(newAllow) {
      return this.connection
        .invoke("UpdateAllowUncLocationsGlobal", newAllow)
        .then((response) => {
          if (response.resultKind === "Success") {
            const updated = response.entity;
            this.fileBrowser = updated;
            this.showSuccess('Always allow UNC Locations updated.');
          } else {
            this.showFailure(response.reason);
          }
        })
        .catch(reason => {
          this.showFailure(reason);
        });
    },
    updateLimitObjectStorageResults(newLimitObjectStorageResults) {
      return this.connection
        .invoke("UpdateLimitObjectStorageResults", newLimitObjectStorageResults)
        .then((response) => {
          if (response.resultKind === "Success") {
            const updated = response.entity;
            this.fileBrowser = updated;
            this.showSuccess('Limit object storage results updated.');
          } else {
            this.showFailure(response.reason);
          }
        })
        .catch(reason => {
          this.showFailure(reason);
        });
    },
    removeOverlayFile() {
      if (confirm('Are you sure you want to remove current overlay?') === false) {
        return;
      }
      return axios
        .post(ConnectionService.getUrl('/api/Settings/RemoveOverlayFile'))
        .then((response) => {
          if (response.data.resultKind === "Success") {
            this.showSuccess('Overlay file successfully');
            this.overlay = this.getDefaultOverlay();
          } else {
            this.showFailure(response.data.reason);
          }
        })
        .catch(this.showFailure);
    },
    getDefaultOverlay() {
      return {
        topRatio:       0,
        leftRatio:      0,
        fileName:       "",
        fileContent:    "",
        originalWidth:  512,
        originalHeight: 128,
        widthRatio:     0,
        heightRatio:    0,
        type:           "Svg"
      }
    },
    mappingToString(mapping) {
      function getLabelMapping(label) {
        return `${label}: ${mapping[label.toLowerCase()] + 1}`;
      }

      return ["L", "R", "C", "LFE", "Ls", "Rs"].map(getLabelMapping).join(", ");
    },
    updateOverlay(newOverlay) {
      this.showSuccess("Overlay updated.");
      this.overlay = newOverlay;
      this.overlayImgSrcType = this.overlay.type == 'Svg' ? 'data:image/svg+xml;base64,' : 'data:image/png;base64,'
    },
    updateImageSequence(newImageSequence) {
      return this.connection
        .invoke("UpdateImageSequence", newImageSequence)
        .then((response) => {
          if (response.resultKind === "Success") {
            const updated = response.entity;
            this.imageSequence = updated.imageSequence;
            this.container = updated;
            this.showSuccess('Image Sequence updated.');
          } else {
            this.showFailure(response.reason);
          }
        })
        .catch(reason => {
          this.showFailure(reason);
        });
    },
    handleRestartRequired() {
      this.connection.on("IsRestartNeeded", newRestartNeeded => {
        this.isRestartNeeded = newRestartNeeded;
      });
    },
    handleIsAdminPasswordChangeNeeded() {
      this.connection.on("IsAdminPasswordChangeNeeded", newAdminPasswordChangeNeeded => {
        this.adminPasswordChangeNeeded = newAdminPasswordChangeNeeded;
      });
    },
    updateShowDeinterlace(newShowDeinterlace) {
      return this.connection
        .invoke("UpdateShowDeinterlace", newShowDeinterlace)
        .then((response) => {
          if (response.resultKind === "Success") {
            const updated = response.entity;
            this.encode.showDeinterlace = updated.showDeinterlace;
            this.encode = updated;
            this.showSuccess('Show Deinterlace updated.');
          } else {
            this.showFailure(response.reason);
          }
        })
        .catch(reason => {
          this.showFailure(reason);
        });
    },
    updateShowBestQuality(newShowBestQuality) {
      return this.connection
        .invoke("UpdateShowBestQuality", newShowBestQuality)
        .then((response) => {
          if (response.resultKind === "Success") {
            const updated = response.entity;
            this.encode.showBestQuality = updated.showBestQuality;
            this.encode = updated;
            this.showSuccess('Show Best Quality updated.');
          } else {
            this.showFailure(response.reason);
          }
        })
        .catch(reason => {
          this.showFailure(reason);
        });
    },
    editLocation(path){
      console.log(this.$refs[path][0].openDialog())
    },
    async getPlugins() {
      this.plugins = await this.connection.invoke("GetPluginInfos");
    },
    pluginTypeName(type) {
      switch (type) {
      case "Plugin": return "Plugin";
      case "ClipListPlugin": return "Clip list plugin";
      default: return type;
      }
    },
    getPluginsDeferred() {
      setTimeout(async () => {
        await this.getPlugins();
      }, 1000);
    },
    async uploadPlugin() {
      let input = document.createElement("input");
      input.type = "file";
      input.multiple = false;
      input.accept = ".cs";

      input.onchange = async () => {
        if (input.files.length <= 0) return;

        let formData = new FormData();
        formData.append("pluginFile", input.files[0]);

        try {
          const response = await axios.post("/api/Plugins/UploadPlugin", formData);

          this.showSuccess(`Plugin '${response.data.pluginName}' uploaded`);

          this.getPluginsDeferred();
        } catch(e) {
          let failureMsg = ["Could not upload plugin."];

          if (e.response?.status == 400 && e.response?.data?.errors?.length > 0) {
            failureMsg = ["Could not upload plugin.", "", ...e.response.data.errors];
          }

          this.showFailure(failureMsg, -1);
        }
      }

      input.click();
    },
    async removePlugin(name) {
      await this.connection.invoke("RemovePlugin", name);

      this.getPluginsDeferred();
    },
    exportSettings() {
      const link = document.createElement('a');
      link.href = `${window.location.origin}/api/settings/exportSettings/`;
      link.download = true;
      link.click();
    },
    async onImportSettings(event){
      const files = event.target.files;
      if (!files) {
        return;
      }

      const formData = new FormData();
      for(var i = 0; i < files.length; i++) {
        formData.append('settingFiles', files[i]);
      }

      await axios.post(
        `/api/settings/importSettings`,
        formData,
      ).then(response => {
        let responseText = '';
        for(var i = 0; i < response.data.length; i++) {
          responseText += response.data[i] + "\n";
        }
        this.showSuccess(responseText);
        this.isRestartNeeded = true;
      }, () => {
        this.showFailure("An error ocurred during import files");
      });
    }
  }
}
</script>

<style scoped>
.overlay-container {
  width  : 50%;
  margin : 0 auto;
}

.gpu-preset {
  display        : flex;
  flex-direction : row;
}

.settings {
  padding-top : 20px;
}

.settings-header-top {
  display        : flex;
  flex-direction : row;
  align-items    : center;
}

    .settings-form {
      margin-left : 10px;
      padding-top : 30px;
      width       : 75%;
    }

    .settings-form > {
      padding-top : 20px;
    }

    .settings-header {
      display         : flex;
      flex-direction  : row;
      justify-content : space-between;
      padding-bottom  : 10px;
    }

    .users-settings {
      padding-top : 16px;
    }

    .overlay {
      display: block;
      width: auto;
      height: auto;
      max-width: 100%;
      max-height: 100%;
      background : url(
      data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAKElEQVQoU2NctWrVfwY0oKSkhC7EwDgUFJ45cwbDM/fu3cP0zBBQCACDvi7rftyl7QAAAABJRU5ErkJggg==
      ) repeat;
    }

    .channel-mappings-table-edit {
      display        : flex;
      flex-direction : row;
    }

    .success-snackbar {
      display         : flex;
      flex-direction  : row;
      justify-content : center;
    }

</style>
